import React, { useEffect, useMemo, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { showErrorToast } from "../components/toasts/ToastService";
import { useOrderSave } from "../context/OrderSaveContext";

const SPECIAL_NEED_FIELD_MAP = {
    spn_nose_cutout: "hmeliorder_spn_nose_cutout",
    spn_hole_punch: "hmeliorder_spn_hole_punch",
    spn_deco_stitch: "hmeliorder_spn_deco_stitch",
    spn_tongue: "hmeliorder_spn_tongue",
};
const SPECIAL_NEED_FIELDS = Object.values(SPECIAL_NEED_FIELD_MAP);
const normalizeSpecialNeedValue = (value) => (Number(value) === 1 ? 1 : 0);

const SpecialisIgeny = () => {
    const navigate = useStepNavigate();
    const { order, updateOrder } = useOrder();
    const [loading, setLoading] = useState(false);
    const { saveOrder } = useOrderSave();

    const specialNeedsRaw = order.model_options?.special_needs_for_model;
    const specialNeeds = useMemo(() => {
        if (Array.isArray(specialNeedsRaw)) return specialNeedsRaw;
        if (specialNeedsRaw && typeof specialNeedsRaw === "object") return Object.values(specialNeedsRaw);
        return [];
    }, [specialNeedsRaw]);

    const [specialNeedValues, setSpecialNeedValues] = useState(() => {
        const current = order.hmeliorder || {};
        return SPECIAL_NEED_FIELDS.reduce((acc, fieldName) => {
            acc[fieldName] = normalizeSpecialNeedValue(current[fieldName]);
            return acc;
        }, {});
    });

    useEffect(() => {
        const current = order.hmeliorder || {};
        setSpecialNeedValues((prev) =>
            SPECIAL_NEED_FIELDS.reduce((acc, fieldName) => {
                acc[fieldName] = normalizeSpecialNeedValue(current[fieldName] ?? prev[fieldName]);
                return acc;
            }, {})
        );
    }, [order.hmeliorder]);

    const mappedOptions = useMemo(
        () =>
            specialNeeds
                .map((need, idx) => {
                    const fieldName = SPECIAL_NEED_FIELD_MAP[need.special_need_code];
                    if (!fieldName) return null;
                    return {
                        key: `${need.special_need_code ?? idx}`,
                        label: need.special_need_name,
                        code: need.special_need_code,
                        fieldName,
                        disabled: Boolean(need.disabled),
                    };
                })
                .filter(Boolean),
        [specialNeeds]
    );

    const handleOptionSelected = (code) => {
        const fieldName = SPECIAL_NEED_FIELD_MAP[code];
        if (!fieldName) return;
        const nextValue = (specialNeedValues[fieldName] ?? 0) === 1 ? 0 : 1;

        setSpecialNeedValues((prev) => ({ ...prev, [fieldName]: nextValue }));
        updateOrder({
            ...order,
            hmeliorder: {
                ...(order.hmeliorder || {}),
                [fieldName]: nextValue,
            },
        });
    };

    const handleClearSelections = () => {
        const cleared = SPECIAL_NEED_FIELDS.reduce((acc, fieldName) => {
            acc[fieldName] = 0;
            return acc;
        }, {});

        setSpecialNeedValues(cleared);
        updateOrder({
            ...order,
            hmeliorder: {
                ...(order.hmeliorder || {}),
                ...cleared,
            },
        });
    };

    const isNoneSelected = useMemo(
        () => SPECIAL_NEED_FIELDS.every((fieldName) => (specialNeedValues[fieldName] ?? 0) === 0),
        [specialNeedValues]
    );

    const handleNext = async () => {
        setLoading(true);

        const persistedValues = SPECIAL_NEED_FIELDS.reduce((acc, fieldName) => {
            acc[fieldName] = specialNeedValues[fieldName] ?? 0;
            return acc;
        }, {});

        const updatedOrder = {
            ...order,
            hmeliorder: {
                ...(order.hmeliorder || {}),
                ...persistedValues,
            },
        };

        try {
            updateOrder(updatedOrder);
            const result = await saveOrder(updatedOrder);

            if (result?.success === false) {
                showErrorToast(result?.message || "Hiba történt a rendelés mentése során!");
                return;
            }

            navigate("/EgySzinu");
        } catch (err) {
            showErrorToast("Hiba történt a rendelés mentése során: " + (err?.message || err));
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate("/RogzitesTipus");
    };

    return (
        <TablerLayout title="Speciális igények">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-body">
                        <div className="mb-4">
                            {!mappedOptions.length ? (
                                <div className="alert alert-warning text-center mb-0">
                                    Nem választható speciális igény.
                                </div>
                            ) : (
                                <div className="row g-4">
                                    {mappedOptions.map((option) => {
                                        const isSelected = (specialNeedValues[option.fieldName] ?? 0) === 1;
                                        return (
                                            <div className="col-12 col-sm-6 col-lg-6" key={option.key}>
                                                <button
                                                    type="button"
                                                    className={`btn w-100 py-3 px-3 shadow-sm d-flex flex-column align-items-center fw-bold
                                                        ${isSelected ? "btn-primary" : "btn-outline-primary bg-white text-primary border-primary"}`}
                                                    style={{
                                                        fontSize: 20,
                                                        fontWeight: 600,
                                                        borderWidth: 2,
                                                        minHeight: 100,
                                                        cursor: option.disabled ? "not-allowed" : "pointer",
                                                        opacity: option.disabled ? 0.6 : 1,
                                                    }}
                                                    onClick={() => !option.disabled && handleOptionSelected(option.code)}
                                                    disabled={option.disabled}
                                                    title={option.disabled ? "Ez a speciális igény nem elérhető" : ""}
                                                >
                                                    {option.label}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="mb-4 text-center">
                            <button
                                type="button"
                                className={`btn w-100 py-4 px-3 shadow-sm d-flex flex-column align-items-center fw-bold
                                    ${isNoneSelected ? "btn-secondary text-white" : "btn-outline-secondary bg-white text-secondary border-secondary"}`}
                                style={{
                                    fontSize: 20,
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    minHeight: 100,
                                }}
                                onClick={handleClearSelections}
                                disabled={loading}
                            >
                                Nincs különleges igény
                            </button>
                        </div>
                    </div>
                    
                </div>
            </div>
            <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton side="left" label="Vissza" onClick={handleBack} />
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={handleNext}
                            disabled={loading}
                        />
                    </div>
        </TablerLayout>
    );
};

export default SpecialisIgeny;
